// The four buttons that walk the game backwards, as a value (§29 U4).
//
// Pure, with a colocated test, beside a zone that only renders it — the
// building-ui split. What is worth testing here is which buttons may act at
// each end of the log, what Latest says about how far behind you are, when it
// is allowed to flash and when it is the way out; none of that needs a
// browser.

import type { IconName } from "./kit";

/**
 * How long Latest pulses after a click was refused for looking back.
 *
 * About a second, which is one unmistakable beat: the flash used to be an
 * opacity dip and read as a render glitch, so it is now a ring and a swell on
 * a button already drawn in brand — big enough that the eye goes to it from
 * the pill it is the answer to.
 */
export const FLASH_MS = 1000;

export interface HistoryTransportInput {
  /** How many boards there are to look at: First, then one per step. */
  count: number;
  /** Which one is on screen, or null while that is the live board. */
  view: number | null;
  /** Log commands landed since the viewed step — the "+N" on Latest. */
  ahead: number;
  /** A click has just been refused for looking back. */
  flashing: boolean;
}

export interface TransportButton {
  /** Its `data-testid`, which is API. */
  id: "history-first" | "history-back" | "history-forward" | "history-latest";
  /** Its name, on the hover card and as the icon's accessible name. */
  label: string;
  /**
   * The picture it draws, named and never drawn here.
   *
   * The playback set, because a transport is what this is: whoever has used a
   * video player knows which of the four goes to the start, and the arrows
   * these replaced (`⏮ ◀ ▶`) were text glyphs at the mercy of the visitor's
   * font fallback — the failure `PIECE_ICON` exists for.
   */
  icon: IconName;
  enabled: boolean;
  /** The step it goes to; **null is the live board**. */
  to: number | null;
  /** Its hover card — the reason when it is off, what it does when it is on. */
  card: string;
}

export interface HistoryTransportModel {
  buttons: TransportButton[];
  /** "+3" on Latest, or null when you are on it or nothing has landed. */
  behind: string | null;
  /** Latest is flashing: somebody tried to play from an old board. */
  flash: boolean;
  /**
   * Latest is drawn as the accent button — the way out, for the whole time
   * there is something to get out of.
   *
   * **Not the flash.** The flash is an answer to one refused click and is
   * gone in a second; this is the standing fact that the board on screen is
   * not the game, and a player who never clicks anything still has to be able
   * to see it. Playtest found the flash alone: "the pill does appear but the
   * 'latest' button does not signify importance anywhere or anyhow at all."
   */
  accent: boolean;
}

const NOTHING = "Nothing has happened yet.";
const AT_START = "You are looking at the opening position.";
const AT_LATEST = "You are looking at the live board.";

/**
 * **Back from Latest is the step before the live board**, not the live board
 * itself — the last step *is* Latest, so treating the view as `count - 1`
 * while live makes one press of Back do something rather than nothing.
 */
export function historyTransport(input: HistoryTransportInput): HistoryTransportModel {
  const { count, view, ahead, flashing } = input;
  const last = count - 1;
  const at = view ?? last;
  const viewing = view !== null;
  const canBack = at > 0;

  return {
    buttons: [
      {
        id: "history-first",
        label: "First",
        icon: "skipPrevious",
        enabled: canBack,
        to: 0,
        card: canBack ? "Back to the opening position" : count > 1 ? AT_START : NOTHING
      },
      {
        id: "history-back",
        label: "Back",
        icon: "fastRewind",
        enabled: canBack,
        to: at - 1,
        card: canBack ? "The move before this one" : count > 1 ? AT_START : NOTHING
      },
      {
        id: "history-forward",
        label: "Forward",
        icon: "fastForward",
        enabled: viewing,
        to: at + 1,
        card: viewing ? "The move after this one" : AT_LATEST
      },
      {
        id: "history-latest",
        label: "Latest",
        icon: "skipNext",
        enabled: viewing,
        to: null,
        card: viewing ? "Back to the live board, where you can play" : AT_LATEST
      }
    ],
    // Only while looking back: on the live board there is nothing to be behind.
    behind: viewing && ahead > 0 ? `+${ahead}` : null,
    // **Only while looking back**, for the same reason: a flash is the answer
    // to "press Latest to play", which is not an instruction worth giving to
    // somebody already standing on it.
    flash: flashing && viewing,
    // Same condition, different clock: on for as long as the view is, not for
    // as long as the flash is.
    accent: viewing
  };
}
